import {Inject, Injectable} from '@angular/core';
import {
    WS_BASE_URL,
    WS_SERVICE_ENDPOINT_URL,
    WS_SERVICE_SCOPE_DEFAULT_VALUE,
    WS_SERVICE_VERSION_DEFAULT_URL
} from '../shared/tokens';
import {Subject} from 'rxjs';
import {CapMeldung} from '../shared/domain/cap-meldung';
import {Parser} from 'xml2js';
import {CapMeldungDetails} from '../shared/domain/cap-meldung-details';
import {CapMeldungParameter} from '../shared/domain/cap-meldung-parameter';
import {CapMeldungResource} from '../shared/domain/cap-meldung-resource';
import {CapMeldungArea} from '../shared/domain/cap-meldung-area';
import {AckMessage} from '../shared/domain/ack-message';
import * as md5 from 'md5';
import { ParsedMessageEventData } from './parsed-message-event-data';
import {ImageResourceExtension} from '../shared/domain/image-resource_extension';
import {toNumbers} from '@angular/compiler-cli/src/diagnostics/typescript_version';

type AckType = 'ACK_RECEIVED' | 'ACK_DELIVERED';

function deepCopy<T>(object: T): T {
    return JSON.parse(JSON.stringify(object));
}

@Injectable()
export class CapMeldungService {

    private capMeldungWebsocket?: WebSocket;

    private readonly capMeldungSubject = new Subject<CapMeldung>();

    #emptyCapMeldungenReceived = 0;

    #activeAlertsHandled = false;

    #wsServiceEndpoint?: string;

    #automaticReconnect = true;

    private static deserializeCapMeldung<
        E extends CapMeldung,
    >(
        event: E,
        ref:
            | string
            | null
        ,
        xml:
            | string
            | null
        ,
    ): E {
        console.log('deserialize Meldung');
        if (event != null) {

            event.alert.autoTranslatedField = false;
            event.alert.autoTranslatedSubtextField = false;
            event.alert.autoTranslatedListField = false;

            if (event.alert.sent != null) {
                event.alert.sentDate = new Date(event.alert.sent);
            } else {
                // we have to explicitly set the eventDate to undefined in order to properly
                // display an empty cell in the flexGrid table
                event.alert.sentDate = undefined;
            }

            if (event.alert.info != null) {
                if (!Array.isArray(event.alert.info)) {
                    // if publication is limited to one language, the result of the xml2js is not an array
                    let capMeldungDetail: CapMeldungDetails;
                    capMeldungDetail = JSON.parse(JSON.stringify(event.alert.info));
                    event.alert.info = undefined;
                    event.alert.info = new Array<CapMeldungDetails>();
                    event.alert.info.push(capMeldungDetail);
                }
                event.alert.info.forEach(item => {
                    if (item.effective != null) {
                        item.effectiveDate = new Date(item.effective);
                    } else {
                        item.effectiveDate = undefined;
                    }

                    if (item.expires != null) {
                        item.expiresDate = new Date(item.expires);
                    } else {
                        item.expiresDate = undefined;
                    }

                    if (item.onset != null) {
                        item.onsetDate = new Date(item.onset);
                    } else {
                        item.onsetDate = undefined;
                    }
                });

                event.alert.info.forEach(item => {

                    if (!Array.isArray(item.category)) {
                        const category = item.category;
                        item.category = new Array<string>();
                        item.category.push(category);
                    }

                    if (item.responseType != null) {
                        if (!Array.isArray(item.responseType)) {
                            const responseType = item.responseType;
                            item.responseType = undefined;
                            item.responseType = new Array<string>();
                            item.responseType.push(responseType);
                        }
                    }

                    if (item.eventCode != null) {
                        if (!Array.isArray(item.eventCode)) {
                            let eventCode: CapMeldungParameter;
                            eventCode = JSON.parse(JSON.stringify(item.eventCode));
                            item.eventCode = undefined;
                            item.eventCode = new Array<CapMeldungParameter>();
                            item.eventCode.push(eventCode);
                        }
                    }

                    if (item.parameter != null) {
                        if (!Array.isArray(item.parameter)) {
                            let parameter: CapMeldungParameter;
                            parameter = JSON.parse(JSON.stringify(item.parameter));
                            this.handleParameters(parameter, event, item);
                            item.parameter = undefined;
                            item.parameter = new Array<CapMeldungParameter>();
                            item.parameter.push(parameter);
                        } else {
                            item.parameter.forEach(parameter => {
                                this.handleParameters(parameter, event, item);
                            });
                        }
                    }

                    if (item.resource != null) {
                        if (!Array.isArray(item.resource)) {
                            let resource: CapMeldungResource;
                            resource = JSON.parse(JSON.stringify(item.resource));
                            item.resource = undefined;
                            item.resource = new Array<CapMeldungResource>();
                            item.resource.push(resource);
                        }
                    }

                    if (item.area != null) {
                        if (!Array.isArray(item.area)) {
                            let area: CapMeldungArea;
                            area = JSON.parse(JSON.stringify(item.area));
                            item.area = undefined;
                            item.area = new Array<CapMeldungArea>();
                            item.area.push(area);
                        }

                        item.area.forEach(areaItem => {
                            if (areaItem.circle != null) {
                                if (!Array.isArray(areaItem.circle)) {
                                    let circle: string;
                                    circle = JSON.parse(JSON.stringify(areaItem.circle));
                                    areaItem.circle = undefined;
                                    areaItem.circle = new Array<string>();
                                    areaItem.circle.push(circle);
                                }
                            }
                            if (areaItem.polygon != null) {
                                if (!Array.isArray(areaItem.polygon)) {
                                    let polygon: string;
                                    polygon = JSON.parse(JSON.stringify(areaItem.polygon));
                                    areaItem.polygon = undefined;
                                    areaItem.polygon = new Array<string>();
                                    areaItem.polygon.push(polygon);
                                }
                            }
                            if (areaItem.geocode != null) {
                                if (!Array.isArray(areaItem.geocode)) {
                                    let geocode: CapMeldungParameter;
                                    geocode = JSON.parse(JSON.stringify(areaItem.geocode));
                                    areaItem.geocode = undefined;
                                    areaItem.geocode = new Array<CapMeldungParameter>();
                                    areaItem.geocode.push(geocode);
                                }
                            }
                        });
                    }
                });
            }


            // xml2js converts arrays of single items into items.
            // convert single items back to arrays where required

            event.alert.isNationalMessage = false;

            if (event.alert.code != null) {
                if (!Array.isArray(event.alert.code)) {
                    const code = event.alert.code;
                    event.alert.code = undefined;
                    event.alert.code = new Array<string>();
                    event.alert.code.push(code);
                }

                if (event.alert.code.indexOf('NATIONWIDE_ALERT') >= 0) {
                    event.alert.isNationalMessage = true;
                }
            }

            let rsaKey: string = event.alert.Signature.KeyInfo.KeyValue.RSAKeyValue.Modulus.toLocaleString();
            rsaKey = rsaKey.replace(/(\r\n|\n|\r)/gm, '');
            event.alert.rsaKeyMD5Checksum = md5(rsaKey);

            event.xmlMessage = xml;
        }

        if (ref != null) {
            event.reference = ref;
        }

        return event;
    }

    private static handleParameters<E extends CapMeldung>(parameter: CapMeldungParameter, event: E, item: CapMeldungDetails): void {
        switch (parameter.valueName) {
            case 'EventPictogram':
                event.alert.eventPictogram = parameter.value;
                break;
            case 'ImageResourceExt':
                if (!item.imageResourceExt) {
                    item.imageResourceExt = new Array<ImageResourceExtension>();
                }
                const tmpImageResourceExtension = parameter.value.split('|');

                const imageResource = {
                    index: parseInt(tmpImageResourceExtension[0], 10),
                    size: tmpImageResourceExtension[1],
                    sha1: tmpImageResourceExtension[2],
                    sha256: tmpImageResourceExtension[3],
                    sha512: tmpImageResourceExtension[4]
                };
                item.imageResourceExt.push(imageResource);
                break;
            case 'WebURL':
                if (!item.webURL) {
                    item.webURL = new Array<string>();
                }
                item.webURL.push(parameter.value);
                break;
            case 'TranslatedField':
                event.alert.autoTranslatedField = true;
                if (!item.autoTranslatedField) {
                    item.autoTranslatedField = new Array<string>();
                }
                item.autoTranslatedField.push(parameter.value);
                break;
            case 'TranslatedSubtextField':
                event.alert.autoTranslatedSubtextField = true;
                if (!item.autoTranslatedSubtextField) {
                    item.autoTranslatedSubtextField = new Array<string>();
                }
                item.autoTranslatedSubtextField.push(parameter.value);
                break;
            case 'TranslatedListField':
                event.alert.autoTranslatedListField = true;
                if (!item.autoTranslatedListField) {
                    item.autoTranslatedListField = new Array<string>();
                }
                item.autoTranslatedListField.push(parameter.value);
                break;
        }
    }

    constructor(@Inject(WS_BASE_URL) private readonly wsBaseUrl: string,
                @Inject(WS_SERVICE_ENDPOINT_URL) private readonly wsServiceEndpointBase: string,
                @Inject(WS_SERVICE_VERSION_DEFAULT_URL) private readonly wsServiceEndpointVersion: string,
                @Inject(WS_SERVICE_SCOPE_DEFAULT_VALUE) private readonly wsServiceScope: string) {
        this.setEndpoint(this.wsServiceEndpointVersion, wsServiceScope);
    }

    setEndpoint(version: string, scope: string): void {
        this.#wsServiceEndpoint = this.wsServiceEndpointBase.replace('{{version}}', version);

        if (scope !== '') {
            this.#wsServiceEndpoint = this.#wsServiceEndpoint + '?scope=' + scope;
        }
    }

    consumeEvents(): Subject<CapMeldung> {
        console.log('subscribing to cap meldung websocket on', this.#wsServiceEndpoint);
        this.#automaticReconnect = true;
        if (!this.capMeldungWebsocket || this.capMeldungWebsocket.readyState !== this.capMeldungWebsocket.OPEN) {
            this.capMeldungWebsocket = new WebSocket(this.wsBaseUrl + this.#wsServiceEndpoint);

            this.capMeldungWebsocket.onmessage = (event: MessageEvent) => {
                const data = JSON.parse(event.data) as ParsedMessageEventData;

                const parser = new Parser({explicitArray: false});

                console.log('Received Message of type: ' + data.type);

                if (data.type === 'ACTIVE_ALERTS') {
                    console.log('Received all active publications');
                    console.log(data.body);
                    data.body.alerts.forEach(item => {
                        if (item.alert != null) {
                            parser.parseString(item.alert, (err: unknown, result: CapMeldung) => {
                                if (item.reference != null) {
                                    this.sendAck(item.reference, true, 'ACK_RECEIVED');
                                }
                                this.capMeldungSubject.next(CapMeldungService.deserializeCapMeldung(result, item.reference, item.alert));
                            });
                        } else {
                            console.error('Received empty publication');
                            this.#emptyCapMeldungenReceived = this.#emptyCapMeldungenReceived + 1;
                        }
                    });
                    this.#activeAlertsHandled = true;
                }
                if (data.type === 'ALERT') {
                    console.log('Received new publication');
                    console.log(data.body);
                    if (data.body != null) {
                        if (data.body.alert != null) {
                            parser.parseString(data.body.alert, (err: unknown, result: CapMeldung) => {
                                console.log(result);
                                if (data.body.reference != null) {
                                    this.sendAck(data.body.reference, true, 'ACK_RECEIVED');
                                }
                                this.capMeldungSubject.next(CapMeldungService.deserializeCapMeldung(result, data.body.reference, data.body.alert));
                            });
                        } else {
                            console.error('Received empty publication');
                            this.#emptyCapMeldungenReceived = this.#emptyCapMeldungenReceived + 1;
                        }
                    } else {
                        console.error('Received empty publication');
                        this.#emptyCapMeldungenReceived = this.#emptyCapMeldungenReceived + 1;
                    }
                }
            };
            this.capMeldungWebsocket.onclose = (event: CloseEvent) => {
                console.log('websocket for event notification closed');
                if (this.#automaticReconnect) {
                    this.consumeEvents();
                }
            };
            this.capMeldungWebsocket.onopen = (event: Event) => {
                console.log('websocket for event notification opened');
            };
            this.capMeldungWebsocket.onerror = event => {
                console.log('error on socket: ' + event);
                this.capMeldungSubject.error(event);
            };

            this.capMeldungSubject.unsubscribe = () => {
                console.log('unsubscribe from event websocket');
                if (this.capMeldungWebsocket) {
                    this.capMeldungWebsocket.close();
                }
            };
        }

        return this.capMeldungSubject;
    }

    public forceDisconnect(): void {
        console.log('close websocket for event notification on', this.#wsServiceEndpoint);
        this.#automaticReconnect = false;
        (this.capMeldungWebsocket as WebSocket).close();
    }

    public sendAck(reference: string, ok: boolean, ackType: AckType, message = ''): void {

        const ackMessage: AckMessage = {
            type: ackType,
            body: {
                reference,
                ok,
                message: ( message.length > 0 ? message : undefined)
            }
        };

        console.log(JSON.stringify(ackMessage));
        (this.capMeldungWebsocket as WebSocket).send(JSON.stringify(ackMessage));
    }

    public sendRandomStatistic(reference: string): void {

        const statisticKeys: string[] = ['counter.alert.read',
                                              'counter.alert.push',
                                              'gauge.user.active'];

        const statisticKey: string = statisticKeys[this.getRandomInt(0, statisticKeys.length - 1)];
        const statisticValue: number = this.getRandomInt(1000, 100000);
        const timestamp: number = Date.now();
        const clientId = 'namics.alertswiss.ch';

        const statisticsMessage = {
            type: 'STATISTICS',
            body: {
                timestamp,
                reference,
                clientid: clientId,
                statistics: [{key: statisticKey, value: statisticValue}]
            }
        };

        console.log(JSON.stringify(statisticsMessage));
        (this.capMeldungWebsocket as WebSocket).send(JSON.stringify(statisticsMessage));
    }

    private getRandomInt(min: number , max: number): number {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    public getEmptyCapMeldungenReceived(): number {
        return this.#emptyCapMeldungenReceived;
    }

    public activeAlertsHandled(): boolean {
        return this.#activeAlertsHandled;
    }
}
